import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Wifi, WifiOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATE_BADGE = {
  CONNECTED:       "bg-emerald-100 text-emerald-700 border-emerald-200",
  WAITING_QR:      "bg-amber-100 text-amber-700 border-amber-200",
  WAITING_PAIRING: "bg-amber-100 text-amber-700 border-amber-200",
  CONNECTING:      "bg-blue-100 text-blue-700 border-blue-200",
  AUTHENTICATED:   "bg-cyan-100 text-cyan-700 border-cyan-200",
  DISCONNECTING:   "bg-orange-100 text-orange-700 border-orange-200",
  DISCONNECTED:    "bg-gray-100 text-gray-600 border-gray-200",
  ERROR:           "bg-red-100 text-red-700 border-red-200",
  IDLE:            "bg-gray-100 text-gray-500 border-gray-200",
};

const GATE_MSG = {
  503: "V2 Runtime desativado",
  403: "Tenant não autorizado",
  404: "Endpoint V2 não disponível",
};

function GateError({ status, fallback = "Indisponível" }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
      {GATE_MSG[status] || fallback}
    </div>
  );
}

const ACK_HEALTH_CLS = {
  HEALTHY:      "text-emerald-600",
  DEGRADED:     "text-amber-600",
  BLOCKED:      "text-red-600",
  RECONNECTING: "text-blue-600",
  RECOVERING:   "text-cyan-600",
};

const TL_STYLE = {
  connected:         { cls: "text-emerald-600", label: "Conectado" },
  disconnected:      { cls: "text-red-500",     label: "Desconectado" },
  reconnect:         { cls: "text-blue-500",    label: "Reconexão" },
  ack_degraded:      { cls: "text-amber-500",   label: "ACK degradado" },
  ack_restored:      { cls: "text-emerald-500", label: "ACK restaurado" },
  relay_stuck:       { cls: "text-red-600",     label: "Relay stuck" },
  zombie_detected:   { cls: "text-purple-600",  label: "Zombie detectado" },
  storm_detected:    { cls: "text-red-700",     label: "Storm detectado" },
  storm_cleared:     { cls: "text-orange-400",  label: "Storm limpo" },
  recovery_start:    { cls: "text-blue-400",    label: "Recovery início" },
  recovery_ok:       { cls: "text-emerald-500", label: "Recovery OK" },
  queue_drain:       { cls: "text-cyan-500",    label: "Queue drain" },
  session_restored:  { cls: "text-emerald-700", label: "Sessão restaurada" },
  false_positive:    { cls: "text-gray-400",    label: "Falso positivo" },
  degradation_chain: { cls: "text-red-800",     label: "Cadeia degradação" },
};

const BUCKET_COLOR = {
  "<1s":   "bg-emerald-500",
  "1-3s":  "bg-amber-400",
  "3-5s":  "bg-orange-400",
  "5-10s": "bg-red-400",
  "10s+":  "bg-red-700",
};

function AckLatencyBars({ buckets, total }) {
  if (!buckets || total === 0) {
    return <p className="text-xs text-muted-foreground italic">Sem dados ainda — aguardar primeira relay</p>;
  }
  return (
    <div className="space-y-1">
      {Object.entries(buckets).map(([label, count]) => {
        const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
        const color = BUCKET_COLOR[label] || "bg-gray-400";
        return (
          <div key={label} className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground w-10 shrink-0 font-mono">{label}</span>
            <div className="flex-1 bg-gray-100 rounded h-2 overflow-hidden">
              <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-muted-foreground w-8 text-right tabular-nums">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function RuntimeTimeline({ events }) {
  if (!events || events.length === 0) {
    return <p className="text-xs text-muted-foreground italic">Sem eventos registados ainda</p>;
  }
  return (
    <ol className="space-y-0.5 max-h-48 overflow-y-auto pr-1">
      {[...events].reverse().map((ev, i) => {
        const s = TL_STYLE[ev.type] || { cls: "text-gray-500", label: ev.type };
        const t = ev.ts
          ? new Date(ev.ts).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
          : "—";
        return (
          <li key={i} className="flex items-baseline gap-2 text-xs">
            <span className="text-gray-300 font-mono shrink-0 w-16">{t}</span>
            <span className={`${s.cls} font-medium shrink-0`}>{s.label}</span>
            {ev.code  != null && <span className="text-gray-400">code={ev.code}</span>}
            {ev.score != null && <span className="text-gray-400">score={ev.score}</span>}
            {ev.gen   != null && <span className="text-gray-400">gen={ev.gen}</span>}
          </li>
        );
      })}
    </ol>
  );
}

function DemoStatusCard({ status }) {
  const st     = status.state || "IDLE";
  const cls    = STATE_BADGE[st] || STATE_BADGE.IDLE;
  const ok     = st === "CONNECTED";
  const lt     = status.last_transition;
  const ackCls = ACK_HEALTH_CLS[status.ack_health] || "text-gray-500";
  const rate   = status.ack_success_rate != null
    ? `${Math.round(status.ack_success_rate * 100)}%`
    : "—";
  return (
    <Card className={ok ? "border-emerald-200 bg-emerald-50/30" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {ok
              ? <Wifi className="w-4 h-4 text-emerald-600" />
              : <WifiOff className="w-4 h-4 text-gray-400" />}
            <span className="text-sm font-semibold text-gray-800">
              {ok ? "Demo Runtime Connected" : "Demo Runtime"}
            </span>
          </div>
          <Badge className={`text-xs border ${cls}`}>{st}</Badge>
        </div>
        <dl className="text-xs text-muted-foreground space-y-1">
          <div><span className="font-medium">Tenant:</span> {status.tenant_id || "—"}</div>
          <div><span className="font-medium">Heartbeat:</span> {status.heartbeat_active ? "✓ ativo" : "○ inativo"}</div>
          <div>
            <span className="font-medium">QR:</span>{" "}
            {status.qr_available ? "disponível" : "indisponível"}
            {status.qr_expired ? " (expirado)" : ""}
          </div>
          <div><span className="font-medium">Reconexões:</span> {status.reconnect_count ?? 0}</div>
          {lt && <div><span className="font-medium">Última transição:</span> {lt.from} → {lt.to}</div>}
          {lt?.reason && <div><span className="font-medium">Razão:</span> {lt.reason}</div>}
          {status.pairing_status && status.pairing_status !== "idle" && (
            <div><span className="font-medium">Pairing:</span> {status.pairing_status}</div>
          )}
          {status.instance_id && (
            <div><span className="font-medium">Instance:</span> {status.instance_id}</div>
          )}
        </dl>
        {/* Hardening metrics — read-only, no mutations */}
        {status.storm_active && (
          <div className="mt-2 rounded bg-red-50 border border-red-200 px-2 py-1 text-xs text-red-700 font-medium">
            ⚠ Reconnect storm detectado — cooldown ativo ({Math.round((status.storm_cooldown_remaining_ms || 0) / 1000)}s restantes)
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <div>
            <span className="font-medium">ACK health: </span>
            <span className={ackCls}>{status.ack_health || "—"}</span>
          </div>
          <div><span className="font-medium">ACK rate: </span>{rate}</div>
          <div><span className="font-medium">ACK avg: </span>{status.ack_latency_avg_ms != null ? `${status.ack_latency_avg_ms}ms` : "—"}</div>
          <div><span className="font-medium">ACK samples: </span>{status.ack_latency_total ?? "—"}</div>
          <div><span className="font-medium">Queue depth: </span>{status.queue_depth ?? "—"}</div>
          <div><span className="font-medium">Queue: </span>{status.queue_paused ? "pausada" : "ativa"}</div>
          <div><span className="font-medium">Relay pending: </span>{status.relay_pending_count ?? "—"}</div>
          <div><span className="font-medium">Relay oldest: </span>{status.relay_oldest_pending_ms ? `${status.relay_oldest_pending_ms}ms` : "—"}</div>
          <div><span className="font-medium">Zombie score: </span>{status.zombie_score ?? "—"}</div>
          <div><span className="font-medium">Recovery: </span>{status.recovery_layer || "—"}</div>
          <div><span className="font-medium">Socket gen: </span>{status.socket_generation ?? "—"}</div>
          <div><span className="font-medium">Storm: </span>{status.storm_count ?? 0}×</div>
          {status.ack_failure_streak > 0 && (
            <div className="col-span-2 text-amber-600">
              <span className="font-medium">Fail streak: </span>{status.ack_failure_streak}
            </div>
          )}
          {(status.relay_stuck_total ?? 0) > 0 && (
            <div className="col-span-2 text-red-600">
              <span className="font-medium">Relay stuck total: </span>{status.relay_stuck_total}
            </div>
          )}
          {status.recovery_total_cycles > 0 && (
            <div><span className="font-medium">Recovery cycles: </span>{status.recovery_total_cycles}</div>
          )}
          {status.recovery_success_rate != null && (
            <div><span className="font-medium">Recovery rate: </span>{Math.round(status.recovery_success_rate * 100)}%</div>
          )}
        </div>
        {/* ACK latency histogram — Phase 13 */}
        {status.ack_latency_buckets && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-600 mb-2">Latência ACK</p>
            <AckLatencyBars buckets={status.ack_latency_buckets} total={status.ack_latency_total ?? 0} />
          </div>
        )}
        {/* Runtime event timeline — Phase 18 */}
        {status.timeline_events && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-600 mb-2">
              Timeline de eventos ({status.timeline_total ?? 0} total)
            </p>
            <RuntimeTimeline events={status.timeline_events} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * V2 Runtime tab for Command Center.
 * Calls /whatsapp/v2/runtime/overview and /whatsapp/v2/demo-runtime/status.
 * Fail-soft: each fetch is independent; gate errors shown inline, never crash the page.
 * No sensitive data displayed (no QR raw, no token, no session, no credentials).
 */
export default function V2RuntimeTab() {
  const { authAxios, user } = useAuth();
  const expectedTenantId = user?.restaurant_id || null;
  const [overview, setOverview]       = useState(null);
  const [demoStatus, setDemoStatus]   = useState(null);
  const [overviewErr, setOverviewErr] = useState(null);
  const [demoErr, setDemoErr]         = useState(null);
  const [loading, setLoading]         = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [ovRes, dmRes] = await Promise.allSettled([
      authAxios.get("/whatsapp/v2/runtime/overview"),
      authAxios.get("/whatsapp/v2/demo-runtime/status"),
    ]);
    if (ovRes.status === "fulfilled") { setOverview(ovRes.value.data); setOverviewErr(null); }
    else setOverviewErr(ovRes.reason?.response?.status || 0);
    if (dmRes.status === "fulfilled") {
      const data = dmRes.value.data;
      setDemoStatus(data);
      setDemoErr(null);
      if (expectedTenantId && data?.tenant_id && data.tenant_id !== expectedTenantId) {
        console.warn("[V2RuntimeTab] demo status tenant mismatch", {
          jwt: expectedTenantId,
          runtime: data.tenant_id,
        });
      }
    } else setDemoErr(dmRes.reason?.response?.status || 0);
    setLoading(false);
  }, [authAxios, expectedTenantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">V2 Runtime Overview</h2>
        {overviewErr
          ? <GateError status={overviewErr} />
          : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Total V2",   value: overview?.total_tenants,   cls: "text-gray-800" },
                { label: "Conectados", value: overview?.connected_count,  cls: "text-emerald-600" },
                { label: "Warning",    value: overview?.warning_count,    cls: "text-amber-600" },
                { label: "Critical",   value: overview?.critical_count,   cls: "text-red-600" },
              ].map(({ label, value, cls }) => (
                <Card key={label}>
                  <CardContent className="p-4 text-center">
                    <div className="text-xs text-muted-foreground mb-1">{label}</div>
                    <div className={`text-2xl font-bold ${cls}`}>{value ?? "—"}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        }
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Demo Runtime</h2>
        {demoErr
          ? <GateError status={demoErr} fallback="V2 Demo Runtime indisponível" />
          : demoStatus && (
            <>
              {expectedTenantId && demoStatus.tenant_id && demoStatus.tenant_id !== expectedTenantId && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-3">
                  Tenant JWT ({expectedTenantId.slice(0, 8)}…) ≠ runtime ({demoStatus.tenant_id.slice(0, 8)}…).
                </div>
              )}
              <DemoStatusCard status={demoStatus} />
            </>
          )
        }
      </section>
    </div>
  );
}
